import React from "react";

import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

import { cn } from "@/src/lib/utils";

import type { CellData, RowData } from "@tanstack/react-table";
import type { JSX } from "react";

import type { DataTableColumn } from "./dataTable.types";

interface DataTableColumnHeaderProps<
  TData extends RowData,
  TValue extends CellData,
> {
  column: DataTableColumn<TData, TValue>;
  title: string;
}

interface SortIconProps {
  direction: false | "asc" | "desc";
}

const SORT_ICON_CLASS = "size-4 shrink-0";

function SortIcon({ direction }: SortIconProps): JSX.Element {
  if (direction === "asc") return <ArrowUp className={SORT_ICON_CLASS} />;
  if (direction === "desc") return <ArrowDown className={SORT_ICON_CLASS} />;
  return <ChevronsUpDown className={cn(SORT_ICON_CLASS, "opacity-50")} />;
}

function DataTableColumnHeader<TData extends RowData, TValue extends CellData>({
  column,
  title,
}: DataTableColumnHeaderProps<TData, TValue>): JSX.Element {
  if (!column.getCanSort()) {
    return <span>{title}</span>;
  }

  const sortDirection = column.getIsSorted();

  // Bouton natif sans fond ni padding : il hérite de la police et de l'alignement de l'en-tête.
  return (
    <button
      className="focus-visible:ring-ring/50 inline-flex max-w-full items-center gap-1.5 rounded-sm align-middle outline-none focus-visible:ring-[3px]"
      onClick={() => column.toggleSorting(sortDirection === "asc")}
      type="button"
    >
      <span className="truncate">{title}</span>
      <SortIcon direction={sortDirection} />
    </button>
  );
}

export default DataTableColumnHeader;
